import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ticket, X } from "lucide-react";

import { apiError } from "../../../shared/api/client";
import { couponQueryKeys, couponsApi } from "../../../shared/api/coupons";
import { formatVndWithUnit } from "../../../shared/format";
import { useEntitlement } from "../../../shared/entitlement/useEntitlement";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Áp / gỡ mã giảm giá on an invoice (#18723).
 *
 * <p>Renders nothing at all when the tenant lacks {@code module.coupon} — unlike the catalogue
 * screen, this sits inside a page a Lite clinic uses every day, so an upgrade CTA here would be an
 * advert in the middle of billing rather than an explanation of a feature they went looking for.
 *
 * <p>Also renders nothing once the invoice is paid or void: the backend refuses the change, and
 * offering a control that can only fail is worse than not offering it.
 */
export function InvoiceCouponCard({
  invoiceId,
  invoiceStatus,
  paidTotal,
  onChanged,
}: {
  invoiceId: string;
  invoiceStatus: string;
  paidTotal: number;
  /** Called after a successful apply/remove so the invoice totals refetch. */
  onChanged: () => void;
}) {
  const { enabled } = useEntitlement("module.coupon");
  const qc = useQueryClient();
  const [code, setCode] = useState("");

  const applied = useQuery({
    queryKey: couponQueryKeys.onInvoice(invoiceId),
    queryFn: () => couponsApi.onInvoice(invoiceId),
    enabled,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: couponQueryKeys.onInvoice(invoiceId) });
    onChanged();
  };

  const apply = useMutation({
    mutationFn: () => couponsApi.apply(invoiceId, code.trim()),
    onSuccess: () => {
      setCode("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: () => couponsApi.remove(invoiceId),
    onSuccess: invalidate,
  });

  if (!enabled) return null;

  const editable = paidTotal === 0 && invoiceStatus !== "VOIDED" && invoiceStatus !== "PAID";
  const current = applied.data;

  // Nothing to show and nothing to do — stay out of the way.
  if (!current && !editable) return null;

  return (
    <div className="mt-3 border-t pt-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
        <Ticket className="size-4" />
        Mã giảm giá
      </div>

      {current ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 px-2.5 py-1.5 dark:bg-emerald-950/30">
          <div className="min-w-0">
            <span className="font-mono text-sm font-medium">{current.code}</span>
            <span className="ml-2 text-xs text-muted-foreground">{current.name}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className="font-mono text-sm tabular-nums text-emerald-700 dark:text-emerald-400">
              −{formatVndWithUnit(current.discountVnd)}
            </span>
            {editable && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Gỡ mã ${current.code}`}
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={code}
            placeholder="Nhập mã"
            className="font-mono"
            aria-label="Mã giảm giá"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) apply.mutate();
            }}
          />
          <Button onClick={() => apply.mutate()} disabled={!code.trim() || apply.isPending}>
            {apply.isPending ? "Đang áp…" : "Áp mã"}
          </Button>
        </div>
      )}

      {apply.isError && (
        <p className="mt-1.5 text-sm text-destructive">{apiError(apply.error).detail}</p>
      )}
      {remove.isError && (
        <p className="mt-1.5 text-sm text-destructive">{apiError(remove.error).detail}</p>
      )}
    </div>
  );
}
